import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  AlertTriangle,
  RefreshCw,
  Calendar,
  ClipboardList,
  Users,
  Briefcase,
  MessageSquare,
  Scale,
  FileText,
  ChevronRight,
  Shield,
} from "lucide-react";
import { api } from "@/App";

const iconMap = {
  AlertTriangle,
  RefreshCw,
  Calendar,
  ClipboardList,
  Users,
  Briefcase,
  MessageSquare,
  Scale,
  FileText,
  Shield,
};

export default function Framework() {
  const [pillars, setPillars] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPillars = async () => {
      try {
        const res = await api.get("/pillars");
        setPillars(res.data);
      } catch (error) {
        // Error fetching framework pillars
      } finally {
        setLoading(false);
      }
    };
    fetchPillars();
  }, []);

  return (
    <div data-testid="framework-page">
      {/* Header */}
      <section className="bg-slate-900 text-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <p className="label-uppercase text-orange-400 mb-4">PPS Framework</p>
            <h1 className="text-4xl sm:text-5xl font-bold mb-6" data-testid="framework-title">
              Nine Pillars of Coherent PPS
            </h1>
            <p className="text-lg text-slate-300">
              A systems-based framework for building consistent, compliant, and 
              effective Pupil Personnel Services across your district. Each pillar 
              represents a critical domain where coherence drives outcomes.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Framework Overview */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-16">
            <h2 className="text-2xl lg:text-3xl font-bold text-slate-900 mb-4">
              What Makes a Coherent PPS System?
            </h2>
            <p className="text-slate-600 mb-6">
              Coherence in PPS means that regardless of which building a student attends, 
              which staff member they encounter, or which day they have a need—the 
              response is predictable, appropriate, and documented.
            </p>
            <div className="grid sm:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-4xl font-bold text-orange-500 font-mono mb-2">1</div>
                <p className="text-sm text-slate-600">
                  <strong>Clear protocols</strong> that define who does what, when
                </p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-orange-500 font-mono mb-2">2</div>
                <p className="text-sm text-slate-600">
                  <strong>Data systems</strong> that surface problems early
                </p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-orange-500 font-mono mb-2">3</div>
                <p className="text-sm text-slate-600">
                  <strong>Training & accountability</strong> that ensure consistency
                </p>
              </div>
            </div>
          </div>

          {/* Pillars Grid */}
          <div className="grid gap-6" data-testid="pillars-list">
            {!loading && pillars.map((pillar, index) => {
              const Icon = iconMap[pillar.icon] || Shield;
              return (
                <motion.div
                  key={pillar.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Link
                    to={`/framework/${pillar.slug}`}
                    className="block bg-white border border-slate-200 hover:border-slate-300 hover:shadow-lg transition-all duration-300"
                    style={{ borderLeftColor: pillar.color, borderLeftWidth: "4px" }}
                    data-testid={`pillar-${pillar.slug}`}
                  >
                    <div className="p-6 lg:p-8 flex flex-col lg:flex-row lg:items-center gap-6">
                      <div 
                        className="w-16 h-16 rounded-sm flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${pillar.color}15` }}
                      >
                        <Icon 
                          className="w-8 h-8" 
                          style={{ color: pillar.color }}
                          strokeWidth={1.5}
                        />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-xs font-mono text-slate-400 uppercase">
                            Pillar {pillar.order}
                          </span>
                        </div>
                        <h3 className="text-xl font-bold text-slate-900 mb-2">
                          {pillar.title}
                        </h3>
                        <p className="text-slate-600 mb-4">
                          {pillar.description}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {pillar.non_negotiables?.slice(0, 3).map((item, i) => (
                            <span 
                              key={i}
                              className="text-xs px-3 py-1 bg-slate-100 text-slate-600 rounded-full"
                            >
                              {item}
                            </span>
                          ))}
                          {pillar.non_negotiables?.length > 3 && (
                            <span className="text-xs px-3 py-1 text-slate-500">
                              +{pillar.non_negotiables.length - 3} more
                            </span>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="w-6 h-6 text-slate-400 hidden lg:block flex-shrink-0" />
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Data Domains */}
      <section className="py-16 lg:py-24 bg-slate-50 dot-pattern">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <p className="label-uppercase text-orange-500 mb-2">Data-Driven Leadership</p>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Six Critical Data Domains
            </h2>
            <p className="text-slate-600">
              These are the data areas every PPS leader should monitor. 
              Each connects to multiple framework pillars.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { name: "Attendance", desc: "Early warning patterns, chronic absence rates", link: "/data/attendance-data" },
              { name: "Referrals", desc: "Timeline compliance, evaluation outcomes", link: "/data/referral-data" },
              { name: "LRE", desc: "Placement distribution, movement patterns", link: "/data/lre-data" },
              { name: "Disproportionality", desc: "Risk ratios, equity indicators", link: "/data/disproportionality-data" },
              { name: "Caseloads", desc: "Service delivery, sustainability metrics", link: "/data/caseload-data" },
              { name: "Process Coherence", desc: "Building variability, compliance patterns", link: "/data/process-coherence-data" },
            ].map((domain, index) => (
              <motion.div
                key={domain.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Link
                  to={domain.link}
                  className="block bg-white border border-slate-200 p-6 hover:shadow-md transition-all duration-300"
                  data-testid={`domain-${domain.name.toLowerCase()}`}
                >
                  <h3 className="text-lg font-bold text-slate-900 mb-2">
                    {domain.name}
                  </h3>
                  <p className="text-sm text-slate-600">
                    {domain.desc}
                  </p>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
